import { z } from 'zod/v3';

import {
  businessOwnerTypeSchema,
  inquiryTypeSchema,
  partnerGradeSchema,
  quoteRequestStatusSchema,
  quoteTypeSchema,
  requesterIndustrySchema,
  serviceTypeSchema,
} from './schemas';

// 공통 스키마 re-export
export * from './schemas';

// 일반 견적 요청 폼 관련 re-export
export * from './register-form';

// 멤버십 견적 요청 폼 관련 re-export
export * from './membership-form';

// ============================================================================
// 파트너 관련 스키마
// ============================================================================

/**
 * 파트너 카드 아이템 스키마
 * @description 파트너 목록(B1)에서 사용되는 카드 형태의 데이터
 * @see GET /api/market-place/partners
 */
export const partnerCardItemSchema = z.object({
  /** 파트너 구독 등급 (PRIME_PLUS, PRIME, PRO, BASE) */
  grade: partnerGradeSchema,
  /** 소개글 (예: "20년 경력의 세무 전문가") */
  introduction: z.string(),
  /** 위치 (예: "서울") */
  location: z.string(),
  /** 사무소명 (예: "세무법인 한길") */
  officeName: z.string(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 파트너명/세무사 이름 (예: "김세무") */
  partnerName: z.string(),
  /** 서비스 비용 (월 기장료, 원 단위) */
  serviceFee: z.number(),
  /** 짧은 주소 (예: "강남구") */
  shortAddress: z.string(),
  /** 썸네일 이미지 URL */
  thumbnailImageUrl: z.string().optional(),
});
export type PartnerCardItem = z.infer<typeof partnerCardItemSchema>;

/**
 * 파트너 상세 정보 스키마
 * @description 파트너 상세 조회(B2) 응답
 * @see GET /api/market-place/partners/{partnerId}
 */
export const partnerDetailSchema = z.object({
  /** 사업장 주소 (예: "서울특별시 강남구 테헤란로 123") */
  address: z.string(),
  /** 영업일 (예: "월~금") */
  businessDays: z.string(),
  /** 영업시간 (예: "09:00~18:00") */
  businessHours: z.string(),
  /** 상세주소 (예: "한길빌딩 5층") */
  detailAddress: z.string(),
  /** 상세 이미지 URL 목록 */
  detailImageUrls: z.array(z.string()),
  /** 파트너 구독 등급 */
  grade: partnerGradeSchema,
  /** 휴무일 (예: "토, 일, 공휴일") */
  holidays: z.string(),
  /** 홈페이지 URL */
  homepage: z.string().optional(),
  /** 소개글 */
  introduction: z.string(),
  /** 위치 (지역명) */
  location: z.string(),
  /** 사무소명 */
  officeName: z.string(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 파트너명/세무사 이름 */
  partnerName: z.string(),
  /** 연락처 (예: "02-1234-5678") */
  phone: z.string(),
  /** 서비스 비용 (월 기장료, 원 단위) */
  serviceFee: z.number(),
  /** 썸네일 이미지 URL */
  thumbnailImageUrl: z.string().optional(),
  /** 우편번호 (예: "06134") */
  zipCode: z.string(),
});
export type PartnerDetail = z.infer<typeof partnerDetailSchema>;

/**
 * 파트너 목록 응답 스키마
 * @description 파트너 목록 조회(B1) 응답
 * @see GET /api/market-place/partners
 */
export const partnerListSchema = z.object({
  /** 다음 페이지 존재 여부 */
  hasNextPage: z.boolean(),
  /** 파트너 목록 */
  items: z.array(partnerCardItemSchema),
  /** 마지막 파트너 ID (커서 기반 페이지네이션용) */
  lastId: z.number().optional(),
  /** 전체 파트너 수 */
  totalCount: z.number(),
});
export type PartnerList = z.infer<typeof partnerListSchema>;

/**
 * 추천 파트너 목록 응답 스키마
 * @description 추천 파트너 목록 조회(B3) 응답. 지역 기반 추천
 * @see GET /api/market-place/partners/recommended
 */
export const recommendedPartnerListSchema = z.object({
  /** 다음 페이지 존재 여부 */
  hasNextPage: z.boolean(),
  /** 추천 파트너 목록 */
  items: z.array(partnerCardItemSchema),
  /** 마지막 파트너 ID (커서 기반 페이지네이션용) */
  lastId: z.number().optional(),
});
export type RecommendedPartnerList = z.infer<
  typeof recommendedPartnerListSchema
>;

// ============================================================================
// 견적 응답 관련 스키마
// ============================================================================

/**
 * 견적 요청 생성 Response 스키마
 * @description 견적 요청 생성(B4) 응답
 */
export const createQuoteResponseSchema = z.object({
  /** 응답 메시지 (예: "비교견적 요청이 완료되었습니다.") */
  message: z.string(),
  /** 생성된 견적 요청 ID */
  quoteRequestId: z.number(),
  /** 견적 유형 */
  quoteType: quoteTypeSchema,
});
export type CreateQuoteResponse = z.infer<typeof createQuoteResponseSchema>;

/**
 * 견적 요청 표시 상태
 * @description 내 견적 요청 목록에서 표시되는 상태
 * - SUBMITTED: 제출됨
 * - QUOTE_ARRIVED: 파트너 배정 완료
 * - ANSWER_ARRIVED: 답변 도착
 */
export const displayStatusSchema = z.enum([
  'SUBMITTED',
  'QUOTE_ARRIVED',
  'ANSWER_ARRIVED',
]);
export type DisplayStatus = z.infer<typeof displayStatusSchema>;

/**
 * 내 견적 요청 아이템 스키마
 * @description 내 견적 요청 목록(B5)의 개별 아이템
 * @see GET /api/market-place/partners/my/quote-requests
 */
export const myQuoteRequestItemSchema = z.object({
  /** 채팅방 오픈 횟수 */
  chatRoomOpenCount: z.number(),
  /** 표시 상태 (SUBMITTED, QUOTE_ARRIVED, ANSWER_ARRIVED) */
  displayStatus: displayStatusSchema,
  /** 만료일시 (ISO 8601) */
  expiredAt: z.string(),
  /** 문의 라벨 (예: "세무기장", "부가세 신고 대행 (1회성)") */
  inquiryLabel: z.string(),
  /** 견적 요청 ID */
  quoteRequestId: z.number(),
  /** 견적 유형 */
  quoteType: quoteTypeSchema,
  /** 요청일시 (ISO 8601) */
  requestAt: z.string(),
  /** 발송된 응답 수 */
  sentResponseCount: z.number(),
  /** 서비스 유형 */
  serviceType: serviceTypeSchema,
  /** 전체 응답 수 */
  totalResponseCount: z.number(),
});
export type MyQuoteRequestItem = z.infer<typeof myQuoteRequestItemSchema>;

/**
 * 내 견적 요청 목록 응답 스키마
 * @description 내 견적 요청 목록(B5) 응답
 */
export const myQuoteRequestListSchema = z.object({
  /** 다음 페이지 존재 여부 */
  hasNextPage: z.boolean(),
  /** 견적 요청 목록 */
  items: z.array(myQuoteRequestItemSchema),
  /** 마지막 견적 요청 ID (커서 기반 페이지네이션용) */
  lastId: z.number().optional(),
});
export type MyQuoteRequestList = z.infer<typeof myQuoteRequestListSchema>;

/**
 * 견적 응답 요약 아이템 스키마
 * @description 견적 요청 상세의 responses 배열 아이템 (요약 정보)
 */
export const quoteResponseSummaryItemSchema = z.object({
  /** 사무소명 */
  officeName: z.string(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 견적 응답 ID */
  quoteResponseId: z.number(),
});
export type QuoteResponseSummaryItem = z.infer<
  typeof quoteResponseSummaryItemSchema
>;

/**
 * 받은 견적 아이템 스키마
 * @description 견적 요청 상세(B6)의 sentResponses 배열 아이템
 */
export const receivedQuoteItemSchema = z.object({
  /** 수락일시 (수락 전이면 null) */
  acceptedAt: z.string().optional(),
  /** 파트너의 답변 내용 */
  answerContent: z.string().optional(),
  /** 채팅방 ID */
  chatRoomId: z.number(),
  /** 예상 비용 (월 기장료, 원 단위) */
  estimatedPrice: z.number().optional(),
  /** 파트너 구독 등급 */
  grade: partnerGradeSchema,
  /** 멤버십 활성화 여부 */
  membershipActive: z.boolean(),
  /** 사무소명 */
  officeName: z.string(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 파트너명/세무사 이름 */
  partnerName: z.string(),
  /** 견적 응답 ID (수락/거절 시 사용) */
  quoteResponseId: z.number(),
  /** 견적서 발송일시 */
  sentAt: z.string().optional(),
  /** 견적 응답 상태 */
  status: z.enum(['PENDING', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED']),
  /** 썸네일 이미지 URL */
  thumbnailImageUrl: z.string().optional(),
  /** 읽지 않은 메시지 수 */
  unreadCount: z.number(),
  /** 만료일시 */
  expiredAt: z.string().optional(),
});
export type ReceivedQuoteItem = z.infer<typeof receivedQuoteItemSchema>;

/**
 * 견적 요청 상세 정보 스키마 (requestDetail 필드)
 * @description 견적 요청 상세(B6)의 requestDetail 객체
 */
export const quoteRequestDetailInfoSchema = z.object({
  /** 연 매출 구간 (예: "UNDER_50M") */
  annualRevenue: z.string(),
  /** 사업장 주소 */
  businessAddress: z.string(),
  /** 사업장 상세주소 */
  businessDetailAddress: z.string(),
  /** 사업자 유형 */
  businessOwnerType: businessOwnerTypeSchema,
  /** 사업장 우편번호 */
  businessZipCode: z.string(),
  /** 요청 내용 */
  requestContent: z.string(),
  /** 요청자 업종 */
  requesterIndustry: requesterIndustrySchema,
  /** 요청자 이름 */
  requesterName: z.string(),
  /** 요청자 연락처 */
  requesterPhone: z.string(),
});
export type QuoteRequestDetailInfo = z.infer<
  typeof quoteRequestDetailInfoSchema
>;

/**
 * 견적 요청 상세 응답 스키마
 * @description 견적 요청 상세 조회(B6) 응답
 * @see GET /api/market-place/partners/my/quote-requests/{quoteRequestId}
 */
export const quoteRequestDetailSchema = z.object({
  /** 사업장 지역 코드 (법정동 코드 10자리) */
  businessRegionCode: z.string(),
  /** 만료일시 (ISO 8601) */
  expiredAt: z.string(),
  /** 문의 라벨 (예: "세무기장", "부가세 신고 대행 (1회성)") */
  inquiryLabel: z.string(),
  /** 문의 유형 코드 (BOOKKEEPING, VAT_FILING, ...) */
  inquiryType: inquiryTypeSchema,
  /** 견적 요청 ID */
  quoteRequestId: z.number(),
  /** 견적 유형 (COMPARE, ONE_TO_ONE, GRADE_BASED) */
  quoteType: quoteTypeSchema,
  /** 요청일시 (ISO 8601) */
  requestAt: z.string(),
  /** 요청 상세 정보 */
  requestDetail: quoteRequestDetailInfoSchema,
  /** 견적 응답 요약 목록 */
  responses: z.array(quoteResponseSummaryItemSchema),
  /** 발송된 견적 목록 (상세 정보) */
  sentResponses: z.array(receivedQuoteItemSchema),
  /** 서비스 유형 (TAX) */
  serviceType: serviceTypeSchema,
});
export type QuoteRequestDetail = z.infer<typeof quoteRequestDetailSchema>;

/**
 * 견적 수락 Response 스키마
 * @description 견적 수락(B7) 응답
 * @see POST /api/market-place/partners/my/quote-responses/{quoteResponseId}/accept
 */
export const acceptQuoteResponseSchema = z.object({
  /** 견적 요청 상태 (수락 후 CONSULTING) */
  quoteRequestStatus: quoteRequestStatusSchema,
  /** 수락한 견적 응답 ID */
  quoteResponseId: z.number(),
});
export type AcceptQuoteResponse = z.infer<typeof acceptQuoteResponseSchema>;

/**
 * 견적 요청 요약 Response 스키마
 * @description 견적 요청 요약 정보 (누적 건수)
 * @see GET /api/market-place/partners/quote-requests/summary
 */
export const quoteRequestSummarySchema = z.object({
  /** 비교견적 요청 건수 */
  compareCount: z.number(),
  /** 등급 기반 견적 요청 건수 */
  gradeBasedCount: z.number(),
  /** 1:1 견적 요청 건수 */
  oneToOneCount: z.number(),
  /** 전체 견적 요청 건수 */
  totalCount: z.number(),
});
export type QuoteRequestSummary = z.infer<typeof quoteRequestSummarySchema>;
